import React, { useCallback, useMemo, useEffect, useState } from 'react';
import { FiMinus } from 'react-icons/fi';

import { Flex, Divider, IconButton } from '@chakra-ui/react';
import axios from 'axios';

import { FilterProps } from '~/modules/activityExecution/@types/activity';
import Filter from '~/shared/components/Filter';
import InputChakra from '~/shared/components/InputChakra';
import CheckboxChakra from '~/shared/components/InputChakra/CheckboxChakra';
import RadioChakra from '~/shared/components/InputChakra/RadioChakra';
import SelectChakra from '~/shared/components/InputChakra/SelectChakra';
import api from '~/shared/services/api';

interface FiltroDataItem {
  id: number | string;
  name: string;
}

interface FiltroDTO {
  activity: FiltroDataItem[];
  demand: FiltroDataItem[];
  company: FiltroDataItem[];
  competences: FiltroDataItem[];
}

interface FilterDataItem {
  value: number | string;
  label: string;
}

interface FiltroData {
  activity: FilterDataItem[];
  demand: FilterDataItem[];
  company: FilterDataItem[];
  competences: FilterDataItem[];
}

interface SelectedData {
  company?: string | null;
  demand?: string | null;
  activity?: string | null;
  competence?: string | null;
}

interface Props {
  isOpen: boolean;
  close(): void;
  filters?(data: FilterProps): void;
  filterAttendance?(data: FilterProps): void;
  attendance_id?: number;
  data: FilterProps | null;
  employee: string | number;
  company: string | number;
  btnRef: React.RefObject<HTMLButtonElement>;
  loading?: boolean;
}

const FilterPlanning: React.FC<Props> = ({
  isOpen,
  close,
  filters,
  data,
  employee,
  company,
  btnRef,
  loading = false,
}) => {
  const [dataForm, setDataForm] = useState<FiltroData>({} as FiltroData);
  const [params, setParams] = useState<SelectedData>({} as SelectedData);
  const [filterLoaded, setFilterLoaded] = useState(false);
  const [lockAttendanceInfo, setLockAttendanceInfo] = useState(false);

  const formattedInitialData = useMemo(() => {
    const situation = [];
    if (data?.status_padding) {
      situation.push('1');
    }
    if (data?.status_running) {
      situation.push('2');
    }
    if (data?.status_paused) {
      situation.push('3');
    }
    if (data?.status_finish) {
      situation.push('4');
    }

    /* Verificar como limpar o filtro quando o botão referente for clicado
    na página de planejamentos, e buscar de novo todos os dados dos filtros */

    if (!data) {
      setParams({});
    }

    setLockAttendanceInfo(!!data?.attendance_id);

    return {
      attendance_id: data?.attendance_id,
      situation,
      order: data?.order,
      company_id: data?.company,
      demand_id: data?.demand,
      activity_id: data?.activity,
      competence: data?.competence,
    };
  }, [data]);

  const msgLoading = 'Carregando...';

  useEffect(() => {
    const { CancelToken } = axios;
    const source = CancelToken.source();

    setFilterLoaded(false);

    api
      .get<FiltroDTO>('plannings/data-filter', {
        params: {
          employee,
          ...params,
        },
        cancelToken: source.token,
      })
      .then((response) => {
        // console.log(response);
        setDataForm({
          company: response.data?.company?.map((item) => {
            return {
              value: item.id,
              label: item.name,
            };
          }),
          demand: response.data?.demand?.map((item) => {
            return {
              value: item.id,
              label: item.name,
            };
          }),
          activity: response.data?.activity?.map((item) => {
            return {
              value: item.id,
              label: item.name,
            };
          }),
          competences: response.data?.competences?.map((item) => {
            return {
              value: item.id,
              label: item.name,
            };
          }),
        });
      })
      .finally(() => {
        setFilterLoaded(true);
      });

    return () => {
      source.cancel('Cancelado pelo usuário');
    };
  }, [employee, company, params]);

  const handleClose = useCallback(() => {
    // setParams({});
    close();
  }, [close]);

  // const handle;

  const handleUpdateParam = (
    att: string,
    value: string | number | null,
  ): void => {
    setParams((state) => ({ ...state, [att]: value }));
  };

  const handleFilter = useCallback(
    async (dataSelected?: any) => {
      let orderName;
      const arraySituations: string[] = [];

      /* if(dataSelected?.attendance_id){
        handleUpdateParam()
      } */

      switch (dataSelected?.order) {
        case 'last':
          orderName = 'Mais recente';
          break;
        case 'older':
          orderName = 'Mais antigo';
          break;
        default:
          break;
      }

      dataSelected?.situation?.forEach((element: string) => {
        arraySituations.push(element);
      });

      const filterActive =
        !!dataSelected?.order ||
        arraySituations.length > 0 ||
        !!dataSelected?.attendance_id ||
        !!dataSelected?.company_id ||
        !!dataSelected?.demand_id ||
        !!dataSelected?.competence ||
        !!dataSelected?.activity_id ||
        false;

      const filteringAttendance = !!dataSelected?.attendance_id;

      if (filteringAttendance) {
        dataSelected.company_id = undefined;
        dataSelected.demand_id = undefined;
        dataSelected.activity_id = undefined;
        dataSelected.competence = undefined;

        setParams({
          company: null,
          demand: null,
          activity: null,
          competence: null,
        });
      }

      if (filters) {
        filters({
          attendance_id: dataSelected?.attendance_id,
          order: dataSelected?.order || null,
          status_finish: !!arraySituations.find((item) => item === 'finished'),
          status_padding: !!arraySituations.find((item) => item === 'pedding'),
          status_paused: !!arraySituations.find((item) => item === 'paused'),
          status_running: !!arraySituations.find((item) => item === 'running'),
          company: dataSelected?.company_id,
          demand: dataSelected?.demand_id,
          activity: dataSelected?.activity_id,
          competence: dataSelected?.competence,
          competence_value: dataForm?.competences.find(
            (item) => item.value === dataSelected?.competence,
          )?.label,
          company_name: dataForm?.company.find(
            (item) => Number(item.value) === Number(dataSelected?.company_id),
          )?.label,
          demand_name: dataForm?.demand.find(
            (item) => Number(item.value) === Number(dataSelected?.demand_id),
          )?.label,
          activity_name: dataForm?.activity.find(
            (item) => Number(item.value) === Number(dataSelected?.activity_id),
          )?.label,
          order_name: orderName,
          situation_name: arraySituations,
          filterActivated: filterActive,
        });
      }
    },
    [filters, dataForm],
  );

  return (
    <>
      <Filter
        title="Filtrar Atividades"
        isOpen={isOpen}
        onClose={handleClose}
        finalFocusRef={btnRef}
        submit={(obData) => handleFilter(obData)}
        loading={loading}
        initialData={formattedInitialData}
      >
        <Flex alignItems="end" mb={4}>
          <InputChakra
            label="Cód. Atendimento"
            type="text"
            name="attendance_id"
            placeholder="Filtre pelo nº do atendimento"
            isInline
            sizeCustom="sm"
            mask="number"
            onBlur={(el) => setLockAttendanceInfo(!!el.target.value)}
            reset={() => setLockAttendanceInfo(false)}
          />
          {/*  <IconButton
            aria-label="search"
            title="Buscar"
            variant="ghost"
            colorScheme="blue"
            icon={<FiSearch size={20} />}
            ml={2}
            type="button"
            onClick={() => attendanceIdInput && handleFilter()}
          /> */}
        </Flex>
        {/*  <Divider my={2} /> */}
        <RadioChakra
          name="order"
          label="Ordem"
          options={[
            {
              value: 'last',
              label: 'Mais recente',
            },
            {
              value: 'older',
              label: 'Mais antigo',
            },
          ]}
        />

        <CheckboxChakra
          name="situation"
          description="Status"
          options={[
            {
              id: '1',
              value: 'pedding',
              label: 'Não iniciada',
              variantColor: 'gray',
            },
            {
              id: '2',
              value: 'running',
              label: 'Executando',
              variantColor: 'blue',
            },
            {
              id: '3',
              value: 'paused',
              label: 'Pausada',
              variantColor: 'orange',
            },
            {
              id: '4',
              value: 'finished',
              label: 'Finalizada',
              variantColor: 'green',
            },
          ]}
        />

        <SelectChakra
          label="Empresa"
          name="company_id"
          options={dataForm.company}
          reset={() => handleUpdateParam('company', null)}
          placeholder={
            !filterLoaded && !params.company
              ? msgLoading
              : 'Selecione a empresa'
          }
          disabled={(!filterLoaded && !params.company) || lockAttendanceInfo}
          onChange={(e) => handleUpdateParam('company', e.target.value)}
          width="full"
        />

        <SelectChakra
          label="Demanda"
          name="demand_id"
          reset={() => handleUpdateParam('demand', null)}
          options={dataForm.demand}
          placeholder={
            !filterLoaded && !params.demand ? msgLoading : 'Selecione a demanda'
          }
          disabled={(!filterLoaded && !params.demand) || lockAttendanceInfo}
          onChange={(e) => handleUpdateParam('demand', e.target.value)}
          width="full"
        />

        <SelectChakra
          label="Atividade"
          name="activity_id"
          reset={() => handleUpdateParam('activity', null)}
          options={dataForm.activity}
          placeholder={
            !filterLoaded && !params.activity
              ? msgLoading
              : 'Selecione a atividade'
          }
          disabled={(!filterLoaded && !params.activity) || lockAttendanceInfo}
          onChange={(e) => handleUpdateParam('activity', e.target.value)}
          width="full"
        />

        <SelectChakra
          label="Competência"
          name="competence"
          reset={() => handleUpdateParam('competence', null)}
          options={dataForm.competences}
          placeholder={
            !filterLoaded && !params.competence
              ? msgLoading
              : 'Selecione a competência'
          }
          width="full"
          disabled={(!filterLoaded && !params.competence) || lockAttendanceInfo}
          onChange={(e) => handleUpdateParam('competence', e.target.value)}
        />
      </Filter>
    </>
  );
};

export default FilterPlanning;
